import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Paper,
  Grid,
  Alert,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Stack,
  Collapse,
  Tooltip,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Search as SearchIcon,
  DateRange as DateRangeIcon,
  GetApp as FetchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Assessment as AssessmentIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Assignment as AssignmentIcon,
  Print as PrintIcon,
  LibraryBooks as CombinedIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { loadLabHistory, loadLabResults, labhistoryLoad, loadPatientLabResultsToday, loadPharmacyInfo } from '../BaseURL';
import LabReportDialog from './LabReportDialog';
import LabPrintDialog from './LabPrintDialog';
import LabHistoryEditDialog from './LabHistoryEditDialog';
import { ErrorMessage } from '../Message';
import { generateLabReportHTML } from './LabReportTemplate';

// `loadLabResults` is exported from BaseURL as a plain URL string
// (`URL + 'loadLabResults'`), not as a function. Earlier code in this file
// accidentally called it like a function, which threw "loadLabResults is
// not a function" and silently broke View Report / Print. This helper
// builds the GET URL with the four query params the backend expects.
const buildLoadLabResultsUrl = (patientId, testCategory, date, labNo) => {
  const enc = (v) => encodeURIComponent(v ?? '');
  return `${loadLabResults}?patientId=${enc(patientId)}&testCategory=${enc(testCategory)}&date=${enc(date)}&labNo=${enc(labNo)}`;
};

// Row component with expand/collapse functionality
const LabHistoryRow = ({
  row,
  onViewReport,
  onViewResults,
  onEditResults,
  onEditRow,               // new — opens the self-contained edit dialog
  onViewDateResults,
  onPrintSingleTest,
  onPrintCombinedForDate,
}) => {
  const [open, setOpen] = useState(false);
  const [expandedData, setExpandedData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Theme-aware tokens. The previous implementation relied on `grey.50`,
  // `grey.100`, `primary.lighter` and `success.lighter` which only exist on
  // the default MUI light palette — in dark mode they fall through to plain
  // white blocks. We compute the right shade per mode here so the row and
  // its expand panel both blend with the dashboard background.
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const subtleBg     = isDark ? alpha(theme.palette.common.white, 0.04) : alpha(theme.palette.common.black, 0.02);
  const subtleBgAlt  = isDark ? alpha(theme.palette.common.white, 0.06) : alpha(theme.palette.common.black, 0.04);
  const primaryTint  = alpha(theme.palette.primary.main, isDark ? 0.18 : 0.10);
  const primaryTintHover = alpha(theme.palette.primary.main, isDark ? 0.28 : 0.18);
  const successTint  = alpha(theme.palette.success.main, isDark ? 0.18 : 0.12);
  const successTintHover = alpha(theme.palette.success.main, isDark ? 0.30 : 0.22);

  const handleToggle = async () => {
    if (!open && expandedData.length === 0) {
      setLoading(true);
      try {
        const response = await axios.get(`${labhistoryLoad}/${row.id}`);
        
        if (response.data.success) {
          setExpandedData(response.data.results);
        }
      } catch (error) {
        console.error('Error fetching patient lab history:', error);
      } finally {
        setLoading(false);
      }
    }
    setOpen(!open);
  };

  return (
    <>
      <TableRow
        hover
        sx={{
          '& > *': { borderBottom: 'unset' },
          transition: 'background-color 0.2s ease',
          ...(open && {
            bgcolor: primaryTint,
            '&:hover': { bgcolor: primaryTint }
          })
        }}
      >
        <TableCell>
          <IconButton
            size="small"
            onClick={handleToggle}
            sx={{
              transition: 'transform 0.25s ease, background-color 0.2s ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              bgcolor: open ? 'primary.main' : 'transparent',
              color: open ? 'white' : 'inherit',
              '&:hover': {
                bgcolor: open ? 'primary.dark' : 'action.hover'
              }
            }}
          >
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontWeight: 500 }}>{row.id || '-'}</TableCell>
        <TableCell sx={{ fontWeight: 600 }}>{row.name || '-'}</TableCell>
        <TableCell>
          <Chip
            label={row.gender || '-'}
            size="small"
            variant="outlined"
            sx={{ minWidth: 60, fontWeight: 500 }}
          />
        </TableCell>
        <TableCell>{row.age || '-'}</TableCell>
        <TableCell>
          <Typography
            component="span"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              fontFamily: 'monospace',
              bgcolor: primaryTint,
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.85rem'
            }}
          >
            {row.path_no || '-'}
          </Typography>
        </TableCell>
        <TableCell>{row.dat ? new Date(row.dat).toLocaleDateString() : '-'}</TableCell>
        <TableCell>{row.test_by || '-'}</TableCell>
        <TableCell>
          <Stack direction="row" spacing={1}>
            <IconButton
              color="success"
              onClick={() => onViewReport(row)}
              title="View Full Report"
              size="small"
              sx={{
                bgcolor: successTint,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: successTintHover,
                  transform: 'translateY(-1px)',
                  boxShadow: 1
                }
              }}
            >
              <AssessmentIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="primary"
              onClick={() => onViewDateResults(row)}
              title="View Results for This Date"
              size="small"
              sx={{
                bgcolor: primaryTint,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: primaryTintHover,
                  transform: 'translateY(-1px)',
                  boxShadow: 1
                }
              }}
            >
              <AssignmentIcon fontSize="small" />
            </IconButton>
            {/* Edit — opens the self-contained LabHistoryEditDialog with
                the row's saved parameters loaded from /loadLabResults.
                Works for any historical lab regardless of how old it is,
                unlike the older "Edit Results" button nested inside the
                View-Results-for-Date dialog which depended on the
                patient still being in the current MyPatients list. */}
            {onEditRow && (
              <IconButton
                onClick={() => onEditRow(row)}
                title="Edit this saved lab"
                size="small"
                sx={{
                  color: '#7c3aed',
                  bgcolor: isDark ? alpha('#7c3aed', 0.14) : alpha('#7c3aed', 0.09),
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: isDark ? alpha('#7c3aed', 0.22) : alpha('#7c3aed', 0.14),
                    transform: 'translateY(-1px)',
                    boxShadow: 1,
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                m: 2,
                p: 2.5,
                borderRadius: 2,
                bgcolor: isDark
                  ? alpha(theme.palette.primary.main, 0.06)
                  : alpha(theme.palette.primary.main, 0.04),
                border: '1px solid',
                borderColor: isDark
                  ? alpha(theme.palette.primary.main, 0.25)
                  : alpha(theme.palette.primary.main, 0.18),
                borderLeft: '4px solid',
                borderLeftColor: 'primary.main',
                boxShadow: isDark
                  ? 'inset 0 1px 3px rgba(0,0,0,0.35)'
                  : 'inset 0 1px 3px rgba(0,0,0,0.04)',
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}
              >
                <AssignmentIcon sx={{ color: 'primary.main' }} />
                <Typography
                  variant="subtitle1"
                  component="div"
                  sx={{ color: 'primary.main', fontWeight: 600 }}
                >
                  Lab History for {row.name}
                </Typography>
                {!loading && expandedData.length > 0 && (
                  <Chip
                    label={`${expandedData.length} record${expandedData.length === 1 ? '' : 's'}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {/* Push the combined-print CTA to the right edge. Only shown
                    when the expansion holds more than one test — a single
                    test already has its own per-row print button. */}
                <Box sx={{ flexGrow: 1 }} />
                {!loading && expandedData.length > 1 && (
                  <Tooltip title={`Print every test in this expansion (${expandedData.length}) as one combined report`}>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<CombinedIcon fontSize="small" />}
                      onClick={() => onPrintCombinedForDate && onPrintCombinedForDate(row, expandedData)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 1.5,
                        background: (theme) =>
                          `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        boxShadow: 2,
                        '&:hover': {
                          background: (theme) =>
                            `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                          boxShadow: 4,
                        },
                      }}
                    >
                      Print Combined
                    </Button>
                  </Tooltip>
                )}
              </Stack>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={30} />
                </Box>
              ) : expandedData.length === 0 ? (
                <Alert severity="info" variant="outlined">
                  No additional lab records found for this patient.
                </Alert>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Table size="small" aria-label="patient lab history">
                    <TableHead>
                      <TableRow sx={{ bgcolor: subtleBgAlt }}>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5, borderBottomColor: 'divider' }}>Path Number</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5, borderBottomColor: 'divider' }}>Test Category</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5, borderBottomColor: 'divider' }}>Test Date</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5, borderBottomColor: 'divider' }}>Tested By</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5, borderBottomColor: 'divider' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: 0.5, borderBottomColor: 'divider' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {expandedData.map((historyRow, index) => (
                        <TableRow
                          key={index}
                          hover
                          sx={{
                            transition: 'background-color 0.15s ease',
                            '&:nth-of-type(even)': { bgcolor: subtleBg },
                            '& td': { color: 'text.primary', borderBottomColor: 'divider' },
                          }}
                        >
                          <TableCell>
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 600,
                                color: 'primary.main',
                                fontFamily: 'monospace',
                                fontSize: '0.85rem'
                              }}
                            >
                              {historyRow.path_no}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {historyRow.cat || historyRow.category || '-'}
                          </TableCell>
                          <TableCell>
                            {historyRow.dat ? new Date(historyRow.dat).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>{historyRow.test_by || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={historyRow.status || 'Completed'}
                              color="success"
                              size="small"
                              variant="filled"
                              sx={{ fontWeight: 600, minWidth: 90 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <Tooltip title="View full report for this test">
                                <IconButton
                                  color="success"
                                  onClick={() => onViewReport(historyRow)}
                                  size="small"
                                  sx={{
                                    bgcolor: successTint,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      bgcolor: successTintHover,
                                      transform: 'translateY(-1px)',
                                      boxShadow: 1
                                    }
                                  }}
                                >
                                  <AssessmentIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={`Print only this test (${historyRow.cat || historyRow.category || 'this lab'})`}>
                                <IconButton
                                  color="primary"
                                  onClick={() => onPrintSingleTest && onPrintSingleTest(historyRow, row)}
                                  size="small"
                                  sx={{
                                    bgcolor: primaryTint,
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                      bgcolor: primaryTintHover,
                                      transform: 'translateY(-1px)',
                                      boxShadow: 1
                                    }
                                  }}
                                >
                                  <PrintIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {/* Edit — same pencil we added on the outer
                                  history row, but here it's per-lab so the
                                  tech can edit any of the (up to 15+)
                                  entries a patient has accumulated over
                                  time. Merges the historyRow's per-test
                                  fields with the outer row's identity
                                  (id/name/gender/age) so the load call
                                  has everything it needs. */}
                              {onEditRow && (
                                <Tooltip title="Edit this saved lab">
                                  <IconButton
                                    onClick={() => onEditRow({
                                      ...historyRow,
                                      id:     historyRow.id     || row.id,
                                      name:   historyRow.name   || row.name,
                                      gender: historyRow.gender || row.gender,
                                      age:    historyRow.age    || row.age,
                                    })}
                                    size="small"
                                    sx={{
                                      color: '#7c3aed',
                                      bgcolor: isDark ? alpha('#7c3aed', 0.14) : alpha('#7c3aed', 0.09),
                                      transition: 'all 0.2s ease',
                                      '&:hover': {
                                        bgcolor: isDark ? alpha('#7c3aed', 0.22) : alpha('#7c3aed', 0.14),
                                        transform: 'translateY(-1px)',
                                        boxShadow: 1,
                                      },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const LabHistory = ({ onEditResults }) => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [labHistoryData, setLabHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [selectedPathNo, setSelectedPathNo] = useState('');
  const [selectedResults, setSelectedResults] = useState([]);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);

  // Self-contained edit dialog — opens from the pencil icon on a
  // history row. Independent of the older MyPatients "jump and edit"
  // flow so it works for historical patients too.
  const [editRow,     setEditRow]     = useState(null);
  const [editOpen,    setEditOpen]    = useState(false);
  const currentUserName = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('userInfo') || '{}');
      return u?.name || u?.username || '';
    } catch (_) { return ''; }
  })();
  const handleOpenEdit = (row) => {
    setEditRow(row);
    setEditOpen(true);
  };
  const handleCloseEdit = () => {
    setEditOpen(false);
    // Keep editRow for the fade-out; cleared when the next row opens.
  };

  // New state for LabReportDialog
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportData, setReportData] = useState({});
  const [reportLoading, setReportLoading] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);

  // Print template picker (Standard vs branded Dekyi layout)
  const [printDialogOpen, setPrintDialogOpen]                   = useState(false);
  const [printReportData, setPrintReportData]                   = useState({});
  const [printPatient, setPrintPatient]                         = useState(null);
  const [printUserInfo, setPrintUserInfo]                       = useState(null);
  const [printCategoryInterpretations, setPrintCategoryInterpretations] = useState({});
  const [printReferringDoctor, setPrintReferringDoctor]               = useState('');
  const [printCategoryHistograms, setPrintCategoryHistograms]         = useState({});
  const [printCategoryNarratives, setPrintCategoryNarratives]         = useState({});
  // category name → 'parameter' | 'narrative'. Without this the print
  // template has no way to know a lab was recorded as a description, so a
  // narrative lab printed from here came out as a numeric parameter table
  // (Unit / Status columns, no Impression) instead of the Test Name /
  // Result layout it was entered in.
  const [printCategoryFormats, setPrintCategoryFormats]                = useState({});

  // Load company info on mount
  React.useEffect(() => {
    const loadCompanyData = async () => {
      try {
        const response = await fetch(loadPharmacyInfo);
        const data = await response.json();
        if (data.success && data.results) {
          setCompanyInfo(data.results);
        }
      } catch (error) {
        console.error('Error loading company info:', error);
      }
    };
    loadCompanyData();
  }, []);

  const handleFetchData = async () => {
    if (!fromDate || !toDate) {
      ErrorMessage('Error', 'Please select both from date and to date');
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      ErrorMessage('Error', 'From date cannot be greater than to date');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(loadLabHistory, {
        fromDate,
        toDate
      });

      if (response.data.success) {
        setLabHistoryData(response.data.results);
      } else {
        setLabHistoryData([]);
      }
    } catch (error) {
      console.error('Error fetching lab history:', error);
      setLabHistoryData([]);
      alert('Error fetching lab history data');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  
  const handleViewDateLabResults = async (rowData) => {
    console.log('🔵 [LAB HISTORY] View date results clicked:', { rowData });
    
   
    try {
      // Extract the date from the row data
      const testDate = rowData.dat;
      const patientId = rowData.id;
      
       console.log(testDate)
      if (!testDate) {
        alert('No test date available for this record.');
        return;
      }

      const response = await fetch(loadPatientLabResultsToday, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: patientId,
          date: testDate  // Pass the specific date instead of using today
        })
      });

      const data = await response.json();

      if (data.success && data.reportData && Object.keys(data.reportData).length > 0) {
        // Build a patient object for both the dialog and the print template.
        // Fall back through several possible column spellings on the history
        // row so the branded layout still gets phone/address/order data even
        // when not all columns are present in the history payload.
        const patient = {
          id: rowData.id,
          fullName: rowData.name,
          age: rowData.age,
          gender: rowData.gender,
          tel:     rowData.tel || rowData.phone || rowData.telephone,
          email:   rowData.email,
          address: rowData.address || rowData.location,
          path_no: rowData.path_no,
          referring_doctor: rowData.referring_doctor || rowData.doctor,
        };

        const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};

        setPrintPatient(patient);
        setPrintUserInfo(userInfo);
        setPrintReportData(data.reportData);
        setPrintCategoryInterpretations(data.categoryInterpretations || {});
        setPrintReferringDoctor(data.referringDoctor || '');
        setPrintCategoryHistograms(data.categoryHistograms || {});
        setPrintCategoryNarratives(data.categoryNarratives || {});
        setPrintCategoryFormats(data.categoryFormats || {});
        setPrintDialogOpen(true);
      } else {
        const formattedDate = new Date(testDate).toLocaleDateString();
        alert(`No lab results found for ${formattedDate}.`);
      }
    } catch (error) {
      console.error('❌ [LAB HISTORY] Error fetching date lab results:', error);
      alert('Error fetching lab results: ' + error.message);
    }
  };

  const handleViewReport = async (rowData) => {
    console.log('🔵 [LAB HISTORY] View report clicked:', { rowData });
    
    const patient = {
      id: rowData.id,
      fullName: rowData.name,
      age: rowData.age,
      gender: rowData.gender
    };

    setSelectedPatient(patient);
    setReportDialogOpen(true);
    setReportLoading(true);
    setReportData({});

    try {
      const patientId = rowData.id;
      const testCategory = rowData.cat || rowData.category || 'General';
      const date = rowData.dat;
      const pathNo = rowData.path_no;

      console.log('🔵 [LAB HISTORY] Fetching full report with:', {
        patientId,
        testCategory,
        date,
        pathNo
      });

      const url = buildLoadLabResultsUrl(patientId, testCategory, date, pathNo);
      console.log('🔵 [LAB HISTORY] Request URL:', url);

      const response = await axios.get(url);
      
      console.log('✅ [LAB HISTORY] Response:', response.data);

      if (response.data.success && response.data.results.length > 0) {
        const formattedData = {};
        
        response.data.results.forEach(result => {
          const resultDate = date;
          const category = result.category || testCategory;
          
          if (!formattedData[resultDate]) {
            formattedData[resultDate] = {};
          }
          
          if (!formattedData[resultDate][category]) {
            formattedData[resultDate][category] = [];
          }
          
          formattedData[resultDate][category].push({
            parameter: result.parameterName,
            result: result.result,
            unit: result.unit,
            range: result.range,
            flag: result.flag || 'normal',
            entered_by: result.entered_by
          });
        });

        console.log('✅ [LAB HISTORY] Formatted report data:', formattedData);
        setReportData(formattedData);
      } else {
        console.log('⚠️ [LAB HISTORY] No results found for report');
        setReportData({});
      }
    } catch (error) {
      console.error('❌ [LAB HISTORY] Error fetching lab report:', error);
      console.error('Error details:', error.response?.data);
      setReportData({});
      alert('Error fetching lab report: ' + (error.response?.data?.message || error.message));
    } finally {
      setReportLoading(false);
    }
  };

  const handleCloseReportDialog = () => {
    setReportDialogOpen(false);
    setReportData({});
    setSelectedPatient(null);
  };

  // ── Print: single test only ─────────────────────────────────────────────
  // Wired to the per-row print icon inside the expanded sub-table. Fetches
  // *only* the rows that belong to one test (filtered by path_no + cat +
  // date) and opens the print dialog so the user can pick a template.
  //
  // `parentRow` is the outer history row that holds full patient context
  // (phone, address, referring doctor, etc.) — the inner historyRow from
  // `labhistoryLoad` only carries per-test metadata, so we fall back to the
  // parent when assembling the patient object for the print template.
  const handlePrintSingleTest = async (historyRow, parentRow = null) => {
    try {
      const patientId    = historyRow.id || parentRow?.id;
      const testCategory = historyRow.cat || historyRow.category || parentRow?.cat || 'General';
      const date         = historyRow.dat || parentRow?.dat;
      const pathNo       = historyRow.path_no || parentRow?.path_no;

      if (!patientId || !date) {
        alert('Missing patient or date information for this test.');
        return;
      }

      const url = buildLoadLabResultsUrl(patientId, testCategory, date, pathNo);
      const response = await axios.get(url);

      if (!response.data.success || !response.data.results?.length) {
        alert('No results found for this test.');
        return;
      }

      // Patient context — prefer the outer row (has phone/address/etc.) and
      // fall back to historyRow for the few fields it does carry.
      const ctx = parentRow || historyRow;
      const patient = {
        id:       historyRow.id || ctx.id,
        fullName: ctx.name,
        age:      ctx.age,
        gender:   ctx.gender,
        tel:      ctx.tel || ctx.phone || ctx.telephone,
        email:    ctx.email,
        address:  ctx.address || ctx.location,
        path_no:  historyRow.path_no || ctx.path_no,
        referring_doctor: ctx.referring_doctor || ctx.doctor,
      };

      // Shape into the {date: {category: [params]}} structure both print
      // templates already consume. `impression` rides along on every row —
      // the narrative template picks the first non-empty one and prints it
      // as the conclusion line.
      const formattedData = {};
      const formats = {};
      response.data.results.forEach((r) => {
        const cat = r.category || testCategory;
        if (!formattedData[date]) formattedData[date] = {};
        if (!formattedData[date][cat]) formattedData[date][cat] = [];
        formattedData[date][cat].push({
          parameter:   r.parameterName,
          result:      r.result,
          unit:        r.unit,
          range:       r.range,
          flag:        r.flag || 'normal',
          entered_by:  r.entered_by,
          result_alt:  r.result_alt,
          unit_alt:    r.unit_alt,
          impression:  r.impression || response.data.impression || '',
          // Travels with the row so the template picks the descriptive
          // layout even if the format map below never reaches it.
          lab_format:  r.lab_format || response.data.labFormat || 'parameter',
        });
        // /loadLabResults reports the catalog's lab_format for the category
        // it was asked about, so every row in this fetch shares it.
        formats[cat] = response.data.labFormat === 'narrative' ? 'narrative' : 'parameter';
      });

      const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};
      setPrintPatient(patient);
      setPrintUserInfo(userInfo);
      setPrintReportData(formattedData);
      setPrintCategoryFormats(formats);
      setPrintDialogOpen(true);
    } catch (error) {
      console.error('❌ [LAB HISTORY] Error printing single test:', error);
      alert('Error fetching test results: ' + (error.response?.data?.message || error.message));
    }
  };

  // ── Print: combined for date ────────────────────────────────────────────
  // Wired to the "Print Combined" button at the top of the expanded panel.
  // Walks every loaded sub-row, fetches its parameters, and merges them all
  // into one printReportData payload so the user gets a single multi-test
  // report instead of having to print each row separately.
  const handlePrintCombinedForDate = async (parentRow, expandedRows) => {
    try {
      const rows = Array.isArray(expandedRows) ? expandedRows.filter(Boolean) : [];
      if (rows.length === 0) {
        alert('No tests available to combine.');
        return;
      }

      // Fetch every test's parameters in parallel — much faster than serial
      // for the common 2-5 test case.
      const fetches = rows.map(async (hr) => {
        const cat = hr.cat || hr.category || parentRow?.cat || 'General';
        const date = hr.dat;
        const url = buildLoadLabResultsUrl(hr.id || parentRow.id, cat, date, hr.path_no);
        try {
          const res = await axios.get(url);
          if (res.data?.success && Array.isArray(res.data.results)) {
            return {
              date,
              cat,
              results: res.data.results,
              labFormat: res.data.labFormat === 'narrative' ? 'narrative' : 'parameter',
              impression: res.data.impression || '',
            };
          }
        } catch (_) { /* swallow per-row errors so one bad row doesn't kill the whole print */ }
        return { date, cat, results: [], labFormat: 'parameter', impression: '' };
      });

      const fetched = await Promise.all(fetches);

      const formattedData = {};
      // Per-category layout for the combined report: a batch can mix a
      // numeric panel and a narrative lab, and each has to print in its own
      // shape rather than all of them following the first one's.
      const formats = {};
      fetched.forEach(({ date, cat, results, labFormat, impression }) => {
        if (!date || !results?.length) return;
        results.forEach((r) => {
          const c = r.category || cat;
          if (!formattedData[date]) formattedData[date] = {};
          if (!formattedData[date][c]) formattedData[date][c] = [];
          formattedData[date][c].push({
            parameter:   r.parameterName,
            result:      r.result,
            unit:        r.unit,
            range:       r.range,
            flag:        r.flag || 'normal',
            entered_by:  r.entered_by,
            result_alt:  r.result_alt,
            unit_alt:    r.unit_alt,
            impression:  r.impression || impression || '',
            lab_format:  r.lab_format || labFormat,
          });
          formats[c] = labFormat;
        });
      });

      if (Object.keys(formattedData).length === 0) {
        alert('No results could be loaded for the combined report.');
        return;
      }

      const patient = {
        id:       parentRow.id,
        fullName: parentRow.name,
        age:      parentRow.age,
        gender:   parentRow.gender,
        tel:      parentRow.tel || parentRow.phone || parentRow.telephone,
        email:    parentRow.email,
        address:  parentRow.address || parentRow.location,
        path_no:  parentRow.path_no,
        referring_doctor: parentRow.referring_doctor || parentRow.doctor,
      };

      const userInfo = JSON.parse(localStorage.getItem('userInfo')) || {};
      setPrintPatient(patient);
      setPrintUserInfo(userInfo);
      setPrintReportData(formattedData);
      setPrintCategoryFormats(formats);
      setPrintDialogOpen(true);
    } catch (error) {
      console.error('❌ [LAB HISTORY] Error printing combined:', error);
      alert('Error building combined report: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleViewResults = async (pathNo, rowData) => {
    console.log('🔵 [LAB HISTORY] View results clicked:', { pathNo, rowData });
    
    setSelectedPathNo(pathNo);
    setSelectedRowData(rowData);
    setResultsLoading(true);
    setResultsDialogOpen(true);

    try {
      const patientId = rowData?.id;
      const testCategory = rowData?.cat || rowData?.category || 'General';
      const date = rowData?.dat;
      
      console.log('🔵 [LAB HISTORY] Fetching results with:', {
        patientId,
        testCategory,
        date,
        pathNo
      });

      const url = buildLoadLabResultsUrl(patientId, testCategory, date, pathNo);
      console.log('🔵 [LAB HISTORY] Request URL:', url);

      const response = await axios.get(url);
      
      console.log('✅ [LAB HISTORY] Response:', response.data);

      if (response.data.success && response.data.results.length > 0) {
        // Narrative labs are shown as section + findings, not as a
        // parameter grid with unit/range/flag columns they don't have.
        const labFormat = response.data.labFormat === 'narrative' ? 'narrative' : 'parameter';

        const groupedResults = response.data.results.reduce((acc, result) => {
          const category = result.category || testCategory;
          if (!acc[category]) {
            acc[category] = {
              testName: category,
              patientName: rowData?.name,
              age: rowData?.age,
              gender: rowData?.gender,
              testDate: date,
              enteredBy: result.entered_by,
              generalComment: response.data.generalComment,
              labFormat,
              impression: response.data.impression || '',
              parameters: []
            };
          }
          // A lab saved twice stored a second copy of every row. Show each
          // section once here so the reader isn't presented with the same
          // finding repeated; the historical-edit dialog is where the
          // redundant rows actually get removed.
          const already = labFormat === 'narrative' &&
            acc[category].parameters.some((p) => p.parameterName === result.parameterName);
          if (!already) acc[category].parameters.push(result);
          return acc;
        }, {});

        const formattedResults = Object.values(groupedResults);
        
        console.log('✅ [LAB HISTORY] Formatted results:', formattedResults);
        setSelectedResults(formattedResults);
      } else {
        console.log('⚠️ [LAB HISTORY] No results found');
        setSelectedResults([]);
      }
    } catch (error) {
      console.error('❌ [LAB HISTORY] Error fetching lab results:', error);
      console.error('Error details:', error.response?.data);
      setSelectedResults([]);
      alert('Error fetching lab results: ' + (error.response?.data?.message || error.message));
    } finally {
      setResultsLoading(false);
    }
  };

  const handleCloseResultsDialog = () => {
    setResultsDialogOpen(false);
    setSelectedPathNo('');
    setSelectedResults([]);
  };

  const filteredData = labHistoryData
    .filter(item =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.path_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.id?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (!searchTerm) return 0;
      
      const search = searchTerm.toLowerCase();
      
      const getMatchPriority = (item) => {
        const id = item.id?.toLowerCase() || '';
        const name = item.name?.toLowerCase() || '';
        const pathNo = item.path_no?.toLowerCase() || '';
        
        if (id === search || name === search || pathNo === search) return 0;
        if (id.startsWith(search) || name.startsWith(search) || pathNo.startsWith(search)) return 1;
        return 2;
      };
      
      return getMatchPriority(a) - getMatchPriority(b);
    });

  const paginatedData = filteredData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: 'primary.main' }}>
        Lab History
      </Typography>

      <Paper
        elevation={0}
        sx={{
          mb: 3,
          px: 2,
          py: 1.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          // Theme-aware gradient — the prior version hard-coded grey[50]
          // for the bottom stop which rendered as bright white on the dark
          // dashboard. We feather to a subtle tint of the paper colour
          // instead so the bar reads as a soft surface in either mode.
          background: (theme) => theme.palette.mode === 'dark'
            ? `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.common.white, 0.04)} 100%)`
            : `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={6} sm={3} md={2.5}>
            <TextField
              fullWidth
              size="small"
              label="From"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'background.paper' } }}
            />
          </Grid>

          <Grid item xs={6} sm={3} md={2.5}>
            <TextField
              fullWidth
              size="small"
              label="To"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <DateRangeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'background.paper' } }}
            />
          </Grid>

          <Grid item xs={12} sm={4} md={5}>
            <TextField
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, ID, or path number…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'background.paper' } }}
            />
          </Grid>

          <Grid item xs={12} sm={2} md={2}>
            <Button
              fullWidth
              variant="contained"
              size="medium"
              onClick={handleFetchData}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FetchIcon fontSize="small" />}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': { boxShadow: 2 }
              }}
            >
              {loading ? 'Fetching…' : 'Fetch'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Lab History Results ({filteredData.length} records)
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
              <CircularProgress />
            </Box>
          ) : labHistoryData.length === 0 ? (
            <Alert severity="info">
              {fromDate && toDate ? 'No lab history found for the selected date range.' : 'Please select a date range and click "Fetch Data" to load lab history.'}
            </Alert>
          ) : (
            <>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden'
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow
                      sx={{
                        background: (theme) =>
                          `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                      }}
                    >
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6, width: 56 }} />
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Patient ID</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Patient Name</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Gender</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Age</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Path Number</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Test Date</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Tested By</TableCell>
                      <TableCell sx={{ color: 'white', fontWeight: 700, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: 0.6 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedData.map((row, index) => (
                      <LabHistoryRow
                        key={index}
                        row={row}
                        onViewReport={handleViewReport}
                        onViewResults={handleViewResults}
                        onEditResults={onEditResults}
                        onEditRow={handleOpenEdit}
                        onViewDateResults={handleViewDateLabResults}
                        onPrintSingleTest={handlePrintSingleTest}
                        onPrintCombinedForDate={handlePrintCombinedForDate}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      <LabReportDialog
        open={reportDialogOpen}
        onClose={handleCloseReportDialog}
        reportData={reportData}
        reportLoading={reportLoading}
        selectedPatient={selectedPatient}
        categoryHistograms={printCategoryHistograms}
        categoryNarratives={printCategoryNarratives}
      />

      <LabPrintDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        reportData={printReportData}
        selectedPatient={printPatient}
        userInfo={printUserInfo}
        companyInfo={companyInfo}
        categoryInterpretations={printCategoryInterpretations}
        categoryHistograms={printCategoryHistograms}
        categoryNarratives={printCategoryNarratives}
        categoryFormats={printCategoryFormats}
        referringDoctor={printReferringDoctor}
      />

      <Dialog
        open={resultsDialogOpen}
        onClose={handleCloseResultsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white', fontWeight: 600 }}>
          Lab Results for Path Number: {selectedPathNo}
        </DialogTitle>
        <DialogContent>
          {resultsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
              <CircularProgress />
            </Box>
          ) : selectedResults.length === 0 ? (
            <Alert severity="info">No results found for this path number.</Alert>
          ) : (
            <Box>
              {selectedResults.map((test, testIndex) => (
                <Card key={testIndex} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6" sx={{ color: 'primary.main' }}>
                        {test.testName}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => {
                          // Route through the same self-contained edit
                          // dialog the pencil icon uses. Works for
                          // historical patients too, unlike the old
                          // localStorage-hop into MyPatients (kept as
                          // fallback for callers that pass onEditResults).
                          handleOpenEdit({
                            id:      selectedRowData?.id,
                            name:    test.patientName,
                            path_no: selectedPathNo,
                            dat:     test.testDate,
                            cat:     test.testName,
                            test_by: test.testedBy || selectedRowData?.test_by || '',
                            age:     test.age,
                            gender:  test.gender,
                          });
                          handleCloseResultsDialog();
                        }}
                      >
                        Edit Results
                      </Button>
                    </Box>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">Patient:</Typography>
                        <Typography variant="body1">{test.patientName}</Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">Age/Gender:</Typography>
                        <Typography variant="body1">{test.age} / {test.gender}</Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">Test Date:</Typography>
                        <Typography variant="body1">{new Date(test.testDate).toLocaleDateString()}</Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">Entered By:</Typography>
                        <Typography variant="body1">{test.enteredBy}</Typography>
                      </Grid>
                    </Grid>

                    {test.parameters && test.parameters.length > 0 ? (
                      test.labFormat === 'narrative' ? (
                        // Descriptive lab — the stored text is the result.
                        // Two columns and the clinical Impression, matching
                        // how it was entered and how it prints.
                        <>
                          <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, width: '30%' }}>Section</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>Findings</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {test.parameters.map((param, paramIndex) => (
                                  <TableRow key={paramIndex}>
                                    <TableCell sx={{ fontWeight: 600, verticalAlign: 'top' }}>
                                      {param.parameterName}
                                    </TableCell>
                                    <TableCell sx={{ whiteSpace: 'pre-wrap' }}>{param.result}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          {test.impression && (
                            <Box sx={{ mt: 2 }}>
                              <Typography variant="body2" color="text.secondary">Impression:</Typography>
                              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{test.impression}</Typography>
                            </Box>
                          )}
                        </>
                      ) : (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 600 }}>Parameter</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Result</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Range</TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>Flag</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {test.parameters.map((param, paramIndex) => (
                              <TableRow key={paramIndex}>
                                <TableCell>{param.parameterName}</TableCell>
                                <TableCell sx={{ fontWeight: 500 }}>{param.result}</TableCell>
                                <TableCell>{param.unit}</TableCell>
                                <TableCell>{param.range}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={param.flag}
                                    color={param.flag === 'normal' ? 'success' : 'warning'}
                                    size="small"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      )
                    ) : (
                      <Alert severity="warning">
                        No {test.labFormat === 'narrative' ? 'sections' : 'parameter results'} available for this test.
                      </Alert>
                    )}

                    {test.generalComment && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">General Comment:</Typography>
                        <Typography variant="body1">{test.generalComment}</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResultsDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Historical edit — opens from the pencil icon on any row. Loads
          the saved parameters via /loadLabResults and writes them back
          via /saveLabResults with trans_codes preserved so the backend
          UPDATE path fires. onSaved refreshes the visible table. */}
      <LabHistoryEditDialog
        open={editOpen}
        onClose={handleCloseEdit}
        row={editRow}
        currentUser={currentUserName}
        onSaved={() => { handleFetchData(); }}
      />
    </Box>
  );
};

export default LabHistory;